/**
 * Component: BrowsePage
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R03
 *
 * Description: BROWSE-V1 list page. On mount, calls
 *              `GET /api/v1/browse/guidelines` via the feature's
 *              typed api client and renders one GuidelineListRow per
 *              guideline. Shows a loading skeleton during the
 *              in-flight request, an alert on error, and an empty
 *              state when the store has no rows.
 *
 *              The page is only reachable when `flags.browse` is
 *              true (FeatureRoute wraps the route in routes.tsx)
 *              and the Browse nav entry only appears when the flag
 *              is on (BROWSE-V1-R02 in Layout.tsx).
 *
 * Risk Links: R9
 * FMEA Links: FM9
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, BookOpen, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/api';
import { listGuidelines } from './api';
import { GuidelineListRow } from './components/GuidelineListRow';
import type { GuidelineListItem } from './types';

export function BrowsePage(): React.ReactElement {
  const [guidelines, setGuidelines] = useState<GuidelineListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGuidelines = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await listGuidelines();
      setGuidelines(rows);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchGuidelines();
  }, [fetchGuidelines]);

  return (
    <section aria-labelledby="browse-heading" className="mx-auto max-w-4xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 id="browse-heading" className="text-2xl font-semibold text-foreground">
            Browse Guidelines
          </h1>
          <p className="text-sm text-muted-foreground">
            Clinical guidelines available in the local library.
          </p>
        </div>
      </header>

      {isLoading && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="browse-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading guidelines...
        </div>
      )}

      {!isLoading && error && (
        <Alert variant="destructive" data-testid="browse-error">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Unable to load guidelines</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>{error}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void fetchGuidelines();
              }}
              className="w-fit"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && guidelines.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border p-12 text-center"
          data-testid="browse-empty"
        >
          <BookOpen className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-base font-medium text-foreground">No guidelines available</p>
          <p className="text-sm text-muted-foreground">
            The local guideline library is currently empty. Check back after the next sync run.
          </p>
        </div>
      )}

      {!isLoading && !error && guidelines.length > 0 && (
        <ul className="flex flex-col gap-3" data-testid="browse-list">
          {guidelines.map((guideline) => (
            <li key={guideline.guidelineId}>
              <GuidelineListRow guideline={guideline} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default BrowsePage;
